import { Editor, Transforms, Descendant, Range } from 'slate';
import { ReactEditor } from 'slate-react';
import { HistoryEditor } from 'slate-history';
import { htmlToSlate, slateToHtml } from './SlateSerialisationUtil';

/**
 * Enhanced plugin that handles pasting HTML content with full formatting support
 * from external sources (Gmail, Google Docs, MS Word, etc.)
 * Also handles copying out with HTML formatting preserved.
 */
function withHtmlPaste<T extends Editor>(editor: T): T & ReactEditor & HistoryEditor {
    const e = editor as T & ReactEditor & HistoryEditor;
    const { insertData, setFragmentData } = e;

    e.insertData = (data: DataTransfer) => {
        const html = data.getData('text/html');

        if (html && html.trim()) {
            try {
                const parsed = htmlToSlate(html);

                if (parsed && parsed.length > 0) {
                    const cleanParsed = parsed.map((node) => cleanFontFamily(node));

                    const hasContent = cleanParsed.some((node) => {
                        if (node.type && node.type !== 'paragraph') {
                            return true;
                        }

                        if (node.children && node.children.length > 0) {
                            return node.children.some((child) => {
                                if (child.type === 'link') return true;
                                return child.text && child.text.trim().length > 0;
                            });
                        }
                        return false;
                    });

                    if (!hasContent) {
                        insertData(data);
                        return;
                    }

                    if (e.selection) {
                        if (!Range.isCollapsed(e.selection)) {
                            Transforms.delete(e);
                        }
                    }

                    const isSingleParagraph = cleanParsed.length === 1 && cleanParsed[0].type === 'paragraph';
                    const hasLists = cleanParsed.some((node) => node.type === 'unorderedList' || node.type === 'orderedList');
                    const hasMultipleParagraphs = cleanParsed.filter((node: any) => node.type === 'paragraph').length > 1;

                    if (hasMultipleParagraphs || hasLists) {
                        Transforms.insertNodes(e, cleanParsed);
                    } else if (isSingleParagraph) {
                        const children = cleanParsed[0].children;
                        if (children && children.length > 0) {
                            Transforms.insertFragment(e, children as Descendant[]);
                        }
                    } else {
                        Transforms.insertNodes(e, cleanParsed);
                    }

                    return;
                }
            } catch (error) {
                console.error('Failed to process pasted HTML:', error);
            }
        }

        insertData(data);
    };

    e.setFragmentData = (data: DataTransfer, originEvent?: 'drag' | 'copy' | 'cut') => {
        setFragmentData(data, originEvent);

        const { selection } = e;
        if (!selection) {
            return;
        }

        try {
            const fragment = Editor.fragment(e, selection);

            if (fragment && fragment.length > 0) {
                const html = slateToHtml(fragment);

                if (html) {
                    data.setData('text/html', html);
                }

                const plainText = fragment.map((node) => Node.string(node)).join('\n');
                data.setData('text/plain', plainText);
            }
        } catch (error) {
            console.error('Failed to set fragment data:', error);
        }
    };

    return e;
}

const Node = {
    string: (node: any): string => {
        if (typeof node.text === 'string') {
            return node.text;
        }
        if (node.children && Array.isArray(node.children)) {
            return node.children.map((child: any) => Node.string(child)).join('');
        }
        return '';
    },
};

const cleanFontFamily = (node: any): any => {
    if (!node) return node;

    try {
        if (node.text !== undefined) {
            const { fontFamily, fontSize, textAlign, ...rest } = node;
            return rest;
        } else if (node.children && Array.isArray(node.children)) {
            return {
                ...node,
                children: node.children.map((child: any) => cleanFontFamily(child)),
            };
        }
        return node;
    } catch (error) {
        console.error('Error cleaning fontFamily:', error);
        return node;
    }
};

export default withHtmlPaste;
